// CLIENT PORTAL LIBRARY — TILE THUMBNAILS: what an upload must be (Round 4 §4).
//
// ⚠️⚠️ AN UPLOADED IMAGE IS SERVED TO CLIENTS FROM OUR ORIGIN, SO IT IS THE SAME
// THREAT AS THE HTML REPORTS, IN MINIATURE. A file that CLAIMS to be a PNG and is
// really HTML, served from our origin with a type the uploader chose, runs as us
// — with access to the localStorage where Firebase Auth keeps a session. So:
//
//   - the MAGIC BYTES decide what a file is. The filename's extension and the
//     declared content type are CLAIMS, and all three must agree;
//   - a short allowlist (PNG, JPEG, WebP) — no SVG, which is a document that
//     can carry script, and no GIF, which nothing here needs;
//   - a size cap;
//   - it is served with the DETECTED type (re-detected from the bytes at serve
//     time, never the stored claim) and `X-Content-Type-Options: nosniff`.
//
// Pure and shared: the staff form runs it before uploading (a courtesy) and the
// server runs it again (the gate). ⚠️ Rule 12 still binds: a thumbnail is a
// COVER image, never a client's logo or brand artwork.

export const THUMBNAIL_MAX_BYTES = 2 * 1024 * 1024;

export type ThumbnailType = 'image/png' | 'image/jpeg' | 'image/webp';

const EXTENSIONS: Record<string, ThumbnailType> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

/** The file extension each detected type is stored under. */
export const THUMBNAIL_EXTENSION: Record<ThumbnailType, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
};

/** What the file input offers, so the OS picker greys out everything else. */
export const THUMBNAIL_ACCEPT = 'image/png,image/jpeg,image/webp';

const startsWith = (b: Uint8Array, sig: readonly number[], at = 0) =>
  b.length >= at + sig.length && sig.every((v, i) => b[at + i] === v);

/**
 * What the BYTES are, or null. ⚠️ This is the check that matters: a name or a
 * declared type costs nothing to fake, a PNG signature is what a PNG is.
 */
export function detectThumbnailType(bytes: Uint8Array): ThumbnailType | null {
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png';
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return 'image/jpeg';
  // RIFF....WEBP
  if (startsWith(bytes, [0x52, 0x49, 0x46, 0x46]) && startsWith(bytes, [0x57, 0x45, 0x42, 0x50], 8)) {
    return 'image/webp';
  }
  return null;
}

export type ThumbnailCheck =
  | { ok: true; contentType: ThumbnailType; extension: string }
  | { ok: false; message: string };

export function validateThumbnail(input: {
  filename: string;
  declaredType: string;
  bytes: Uint8Array;
}): ThumbnailCheck {
  const name = input.filename.trim();
  const dot = name.lastIndexOf('.');
  const byName = dot >= 0 ? EXTENSIONS[name.slice(dot + 1).toLowerCase()] : undefined;
  if (!byName) return { ok: false, message: 'Use a PNG, JPEG or WebP image.' };

  const declared = input.declaredType.split(';')[0]!.trim().toLowerCase();
  // Browsers occasionally label a JPEG image/jpg; it is the same claim.
  const byDeclared = declared === 'image/jpg' ? 'image/jpeg' : declared;
  if (byDeclared !== byName) {
    return { ok: false, message: 'That file’s type doesn’t match its name. Use a PNG, JPEG or WebP image.' };
  }

  if (input.bytes.byteLength === 0) return { ok: false, message: 'That file is empty.' };
  if (input.bytes.byteLength > THUMBNAIL_MAX_BYTES) {
    return { ok: false, message: 'Keep the image under 2 MB.' };
  }

  const byBytes = detectThumbnailType(input.bytes);
  if (byBytes !== byName) {
    return {
      ok: false,
      message: 'That file isn’t the image its name says it is. Use a real PNG, JPEG or WebP image.',
    };
  }
  return { ok: true, contentType: byBytes, extension: THUMBNAIL_EXTENSION[byBytes] };
}

/**
 * The serving URL for a stored thumbnail: our own route, keyed by the ENTRY and
 * the upload's object id (which changes on every upload, so the URL is a
 * version and can be cached). Null for a path this module did not write.
 *
 * ⚠️ Paths are `portal-thumbnails/<entryId>/<objectId>.<ext>`; anything else
 * — including a hosted-report path — yields null rather than a URL.
 */
export function portalThumbnailUrl(entryId: string, objectPath: string | null): string | null {
  const objectId = thumbnailObjectId(entryId, objectPath);
  return objectId === null
    ? null
    : `/api/public/portal-library/thumbnail/${encodeURIComponent(entryId)}/${encodeURIComponent(objectId)}`;
}

/** The object id out of a stored path, only if the path belongs to THIS entry. */
export function thumbnailObjectId(entryId: string, objectPath: string | null): string | null {
  if (!objectPath) return null;
  const m = /^portal-thumbnails\/([^/]+)\/([A-Za-z0-9-]+)\.(png|jpg|webp)$/.exec(objectPath);
  if (!m || m[1] !== entryId) return null;
  return m[2]!;
}
